import { useState } from 'react';
import type { MouseEvent } from 'react';
import { Button, Modal, Stack, useTheme } from '@fti/design-system';
import { useDeleteProject } from '../../../api/media-sim/projects';

interface DeleteProjectButtonProps {
  projectId: string;
  projectName: string;
  /** Called after a successful delete so the caller can refresh its list. */
  onDeleted?: () => void;
}

/**
 * Deletes a project, with a confirmation step.
 *
 * Rendered inside a clickable project card, so every click here is stopped from
 * propagating. Without that, opening the confirmation would also navigate into
 * the project being deleted.
 */
export function DeleteProjectButton({
  projectId,
  projectName,
  onDeleted,
}: DeleteProjectButtonProps) {
  const { theme } = useTheme();
  const { deleteProject, loading } = useDeleteProject();
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stop = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const handleOpen = (e: MouseEvent) => {
    stop(e);
    setError(null);
    setModalOpen(true);
  };

  const handleConfirm = async (e: MouseEvent) => {
    stop(e);
    setError(null);
    try {
      await deleteProject(projectId);
      setModalOpen(false);
      onDeleted?.();
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string };
      if (apiErr?.status === 409) {
        // The API refuses while an execution is in flight, and its message
        // explains why, so show that rather than a generic failure.
        setError(apiErr.message || 'This project has a run in progress.');
      } else if (apiErr?.status === 403) {
        setError('Only the project creator or an admin can delete this project.');
      } else {
        setError(apiErr?.message || 'Failed to delete the project.');
      }
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        color="error"
        size="sm"
        onClick={handleOpen}
        aria-label={`Delete ${projectName}`}
      >
        Delete
      </Button>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
        <Modal.Header>
          <h2 style={{ margin: 0, color: theme.colors.text.primary }}>Delete Project</h2>
        </Modal.Header>
        <Modal.Body>
          <Stack gap="sm">
            <p style={{ color: theme.colors.text.primary, margin: 0 }}>
              Delete <strong>{projectName}</strong>? It will no longer appear anywhere
              in MediaSim.
            </p>
            <p style={{ color: theme.colors.text.secondary, margin: 0, fontSize: '0.9rem' }}>
              Its runs and generated articles are kept, so this can be undone if you
              ask engineering. It cannot be undone from here.
            </p>
            {error && (
              <p style={{ color: theme.colors.semantic.error.default, margin: 0 }}>{error}</p>
            )}
          </Stack>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={(e: MouseEvent) => { stop(e); setModalOpen(false); }}>
            Cancel
          </Button>
          <Button variant="solid" color="error" onClick={handleConfirm} loading={loading}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
